import React, { useEffect, useRef, useState } from 'react'
import { useTranslation } from '@i18n'
import styled from 'styled-components'
import tw, { theme } from 'twin.macro'
import {
  CROP_WIDTH,
  CROP_HEIGHT,
  COVER_IMAGE_API_CROPPED_WIDTH,
  COVER_IMAGE_API_CROPPED_HEIGHT,
  HORIZONTAL_COVER_IMAGE_DISPLAY_WIDTH,
  HORIZONTAL_COVER_IMAGE_DISPLAY_HEIGHT,
} from '../_shared/ImageConstant'
import { UndoIcon, CropIcon } from '@components/Icons'
import FileUploadChanger from '../_shared/FileUploadChanger'
import Button, { ButtonSize, ButtonVariant } from '@components/DesignSystem/Button'

function ImageCard(props) {
  const {
    data,
    setSelectedImages,
    setImageEditorVisibility,
    handleFiles,
    onImageEdit,
    handleImageFile,
    articleState,
    updateImageIds,
  } = props
  const { t } = useTranslation('imageInput')
  const file = [data.file]
  const [uploadedImage, setUploadedImage] = useState(data)
  const [uploadErrorVisibility, setUploadErrorVisibility] = useState(false)
  const [cropErrorVisibility, setCropErrorVisibility] = useState(false)

  const i18nDefault = {
    upload: {
      forbidden: '你沒有創作者的權限唷，請確認登入帳號！',
      missing: '系統發生錯誤，請再試一次！',
      missing_field: '系統錯誤發生，請再試一次！',
      unauthorized: '系統錯誤，請再試一次！',
      db_operation_fail: '系統有誤，請再試一次！',
      gcs_operation_fail: '系統發生錯誤，請重新上傳！',
      image_processing_fail: '系統錯誤，請重新上傳！',
      unexpected: '系統有誤，請重新上傳！',
      network: '網路連線錯誤，請確認網路穩定連線後再試一次！',
      httpStatusCodeOnly: `系統發生錯誤(${
        data.upload ? data.upload.statusCode : ''
      })，請再試一次！`,
    },
    edit: {
      forbidden: '你沒有創作者的權限唷，請確認登入帳號！',
      missing: '圖片裁切發生錯誤',
      missing_field: '圖片裁切錯誤發生',
      unauthorized: '圖片裁切錯誤',
      db_operation_fail: '圖片裁切有誤',
      gcs_operation_fail: '圖片裁切發生錯誤',
      image_processing_fail: '圖片裁切錯誤',
      unexpected: '圖片裁切有誤',
      network: '網路連線錯誤，請確認網路穩定連線後再試一次！',
      httpStatusCodeOnly: `圖片裁切發生錯誤(${
        data.edit ? data.edit.statusCode : ''
      })，請再試一次！`,
    },
  }

  const reEdit = (file) => {
    setCropErrorVisibility(false)
    let body
    const croppedSize = {
      height: COVER_IMAGE_API_CROPPED_HEIGHT,
      width: COVER_IMAGE_API_CROPPED_WIDTH,
      fit: 'cover',
    }
    if (data.crop) {
      body = { crop: data.crop, croppedSize }
    } else {
      body = { croppedSize }
    }

    const payload = {
      id: file.id,
      body,
    }
    const handleImageFileArgs = {
      file,
      setSelectedFiles: setSelectedImages,
      id: file.id,
      onImageAction: onImageEdit,
      actionName: 'edit',
      payload,
      tempCropPosition: data.cropPosition,
      cropResult: data.crop,
      croppedSize,
      updateImageIds,
    }
    handleImageFile({ ...handleImageFileArgs })
  }

  const cancelReUpload = () => {
    if (!articleState.imageIds[0]) {
      setSelectedImages([])
    }
    setUploadErrorVisibility(false)
  }

  const cancelReEdit = () => {
    setCropErrorVisibility(false)
  }

  const displayImageRef = useRef(null)

  useEffect(() => {
    setUploadErrorVisibility(false)
    if (data.upload && data.upload.success && data.loaded) {
      setUploadedImage(data)
    }
    if (data.upload && data.upload.errorCodes && data.upload.success === false) {
      setUploadErrorVisibility(true)
    }
    if (data.edit && data.edit.success === false) {
      setCropErrorVisibility(true)
    }
    const displayImage = displayImageRef.current
    const bgImg = new Image()
    bgImg.src = data.originalUrl
    if (
      (data.upload && data.upload.success && !data.edit) ||
      (data.upload && data.upload.success && data.edit && data.edit.success)
    ) {
      const positionXAdjustRatio = HORIZONTAL_COVER_IMAGE_DISPLAY_WIDTH / CROP_WIDTH
      const positionYAdjustRatio = HORIZONTAL_COVER_IMAGE_DISPLAY_HEIGHT / CROP_HEIGHT

      const bgImgX =
        data?.cropPosition && data?.cropPosition?.croppedBgPositionX !== '50%'
          ? `${data?.cropPosition?.croppedBgPositionX * positionXAdjustRatio}px`
          : '50%'
      const bgImgY =
        data?.cropPosition && data?.cropPosition?.croppedBgPositionY !== '50%'
          ? `${data?.cropPosition?.croppedBgPositionY * positionYAdjustRatio}px`
          : '50%'
      bgImg.onload = () => {
        displayImage.style.backgroundImage = `url(${bgImg.src})`
        displayImage.style.backgroundPositionX = bgImgX
        displayImage.style.backgroundPositionY = bgImgY
        setSelectedImages((prevArray) => {
          const result = {
            ...prevArray[0],
            loaded: true,
            backgroundPositionX: bgImgX,
            backgroundPositionY: bgImgY,
          }
          return [result]
        })
      }
    } else if (data.upload && data.upload.success && data.edit && !data.edit.success) {
      bgImg.onload = () => {
        displayImage.style.backgroundImage = `url(${bgImg.src})`
        displayImage.style.backgroundPositionX = data.backgroundPositionX
        displayImage.style.backgroundPositionY = data.backgroundPositionY
        setSelectedImages((prevArray) => {
          const result = {
            ...prevArray[0],
            loaded: true,
          }
          return [result]
        })
      }
    }
  }, [
    data.id,
    data.originalUrl,
    data.cropPosition,
    data.edit,
    data.upload,
    data.backgroundPositionX,
    data.backgroundPositionY,
    setSelectedImages,
    uploadedImage.originalUrl,
  ])

  return (
    <StyledImageCard>
      <DisplayImage
        show={uploadedImage.upload && uploadedImage.upload.success && uploadedImage.loaded}
        ref={displayImageRef}
      />
      <UploadError
        show={uploadErrorVisibility}
        isChangingImage={
          uploadedImage.originalUrl && data.originalUrl !== uploadedImage.originalUrl
        }
      >
        {data.upload &&
          data.upload.errorCodes.map((code, i) => {
            return (
              <p key={i} className="message">
                {t([`envErrorMessage.upload.${code}`, i18nDefault.upload[code]], {
                  statusCode: data.upload.statusCode,
                })}
              </p>
            )
          })}

        <div className="actions">
          <span className="cancel" onClick={() => cancelReUpload()}>
            {t(['cancel', '取消'])}
          </span>
          <StyledRetryButton
            onClick={() => handleFiles(file)}
            size={ButtonSize.small}
            variant={ButtonVariant.primary}
          >
            <StyledUndo />
            <span>{t(['reTry', '再試一次'])}</span>
          </StyledRetryButton>
        </div>
      </UploadError>
      <CropError show={cropErrorVisibility}>
        {data.edit &&
          data.edit.errorCodes.map((code, i) => {
            return (
              <p key={i} className="message">
                {t([`envErrorMessage.edit.${code}`, i18nDefault.edit[code]], {
                  statusCode: data.edit.statusCode,
                })}
              </p>
            )
          })}
        <div className="actions">
          <span className="cancel" onClick={() => cancelReEdit()}>
            {t(['cancel', '取消'])}
          </span>
          <StyledRetryButton
            onClick={() => reEdit(data.file)}
            size={ButtonSize.small}
            variant={ButtonVariant.primary}
          >
            <StyledUndo />
            <span>{t(['reTry', '再試一次'])}</span>
          </StyledRetryButton>
        </div>
      </CropError>
      <Progress
        show={
          (data.upload && data.upload.success === null) ||
          (data.upload &&
            data.upload.success === true &&
            !data.loaded &&
            data.originalUrl !== uploadedImage.originalUrl) ||
          (data.edit && data.edit.success === null) ||
          (data.edit && data.edit.success === true && !data.loaded)
        }
        isChangingImage={
          uploadedImage.originalUrl && data.originalUrl !== uploadedImage.originalUrl
        }
        showProgressBar={!(data.edit && data.edit.success === false)}
        edited={data.edit}
        uploadCompletedPercent={data.upload ? data.upload.completedPercent : 5}
        editCompletedPercent={data.edit ? data.edit.completedPercent : 5}
      >
        <div className="progress-bar-frame">
          <div className="progress-bar" />
        </div>
      </Progress>
      <HoverMask
        show={
          (!data.edit && data.upload && data.upload.success && data.loaded) ||
          (!data.edit && data.upload && data.upload.success === false) ||
          (data.edit && data.edit.success) ||
          (data.edit && data.edit.success === false && !cropErrorVisibility)
        }
      >
        <ActionsContainer>
          <FileUploadChanger inputId="article-cover-image-input" />
          <Action className="edit" onClick={() => setImageEditorVisibility(true)}>
            <CropIcon />
          </Action>
        </ActionsContainer>
      </HoverMask>
    </StyledImageCard>
  )
}

const HoverMask = styled.div<{
  show?: boolean
}>`
  ${tw`absolute w-full h-full`}
  display: ${(props) => (props.show ? 'block' : 'none')};
  bottom: 0;
  background: rgba(0, 0, 0, 0);
  transition: 0.3s;
`

const ActionsContainer = styled.div`
  ${tw`md:hidden items-center justify-center flex-col absolute`}
  right: 24px;
  top: 24px;
  z-index: 1;

  .edit {
    display: none;
  }
  @media (min-width: ${theme`screens.md`}) {
    .edit {
      display: flex;
    }
  }
`
const StyledImageCard = styled.div`
  ${tw`bg-gray-30`}
  ${tw`absolute w-full h-full top-0 left-0`}
  &:hover {
    + .change-image-button {
      display: flex;
    }
  }

  @media (min-width: ${theme`screens.md`}) {
    &:hover ${HoverMask} {
      background: rgba(0, 0, 0, 0.3);
    }
  }

  &:hover ${ActionsContainer} {
    display: flex;
  }
`

const Progress = styled.div<{
  show?: boolean
  isChangingImage?: boolean
  showProgressBar?: boolean
  edited?: boolean
  editCompletedPercent?: number
  uploadCompletedPercent?: number
}>`
  ${tw`absolute`}
  ${tw`flex items-center justify-center`}
  display: ${(props) => (props.show ? 'flex' : 'none')};
  background-color: ${(props) => (props.isChangingImage ? 'rgba(0,0,0,.7)' : '#f7f7f7')};
  left: 0;
  top: 0;
  height: 100%;
  width: 100%;
  z-index: 1;
  .progress-bar-frame {
    display: ${(props) => (props.showProgressBar ? 'block' : 'none')};
    width: 177px;
    height: 4px;
    background-color: #e6e6e6;
    border-radius: 2px;
    .progress-bar {
      ${tw`bg-primary-indigo`}
      height: 100%;
      width: ${(props) =>
        props.edited ? `${props.editCompletedPercent}%` : `${props.uploadCompletedPercent}%`};
      border-radius: 2px;
    }
  }
`

const Action = styled.div`
  ${tw`flex items-center justify-center cursor-pointer rounded-full`}
  background: white;
  width: 36px;
  height: 36px;
  margin-top: 16px;
  svg {
    ${tw`text-gray-600`}
    width: 20px;
    height: 20px;
  }
`

const DisplayImage = styled.div<{
  show?: boolean
}>`
  display: ${(props) => (props.show ? 'block' : 'none')};
  height: 100%;
  background-size: cover;
`

const StyledRetryButton = styled(Button)`
  ${tw`cursor-pointer`}
  margin-left: 20px;
`

const StyledUndo = styled(UndoIcon)`
  ${tw`text-white`}
  width: 14px;
  height: 14px;
  margin-right: 4px;
`

const UploadError = styled.div<{
  show?: boolean
  isChangingImage?: boolean
}>`
  ${tw`text-center absolute`}
  display: ${(props) => (props.show ? 'block' : 'none')};
  background-color: ${(props) => (props.isChangingImage ? 'rgba(0,0,0,.7)' : '#f7f7f7')};
  width: 100%;
  height: 100%;
  z-index: 2;
  left: 0;
  top: 0;
  .message {
    ${tw`text-c2 absolute`}
    color: ${(props) => (props.isChangingImage ? 'white' : '#333333')};
    width: 100%;
    padding: 0 16px;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
  }
  .actions {
    ${tw`flex items-center justify-center absolute`}
    top: 60%;
    left: 50%;
    transform: translate(-50%, -60%);
    .cancel {
      ${tw`text-c2 font-semibold cursor-pointer`}
      color: ${(props) => (props.isChangingImage ? 'white' : '#333333')};
    }
    ${StyledRetryButton} {
      :hover {
        opacity: ${(props) => (props.isChangingImage ? '0.8' : '0.4')};
      }
    }
  }
`

const CropError = styled.div<{
  show?: boolean
}>`
  ${tw`text-center absolute`}
  display: ${(props) => (props.show ? 'block' : 'none')};
  background-color: rgba(0, 0, 0, 0.7);
  width: 100%;
  height: 100%;
  z-index: 2;
  left: 0;
  top: 0;
  .message {
    ${tw`text-white text-center text-c2 absolute`}
    width: 100%;
    padding: 0 16px;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
  }
  .actions {
    ${tw`flex items-center justify-center absolute`}
    top: 60%;
    left: 50%;
    transform: translate(-50%, -60%);
    .cancel {
      ${tw`text-white text-c2 font-semibold cursor-pointer`}
    }
    ${StyledRetryButton} {
      :hover {
        opacity: 0.8;
      }
    }
  }
`

export default ImageCard
